import { AxiosInstance, AxiosResponse } from 'axios';

import { UserDataResponse } from 'types/admin/userData';

interface UserDataPost {
  userId: string;
}
interface ApiResponse<T> {
  data: T;
  isError: boolean;
}
export class ManageUserDataService {
  private axiosInstance: AxiosInstance;
  constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;
  }

  addSupportMembership = async (userId: string) => {
    const response = await this.axiosInstance.post<UserDataPost, AxiosResponse<ApiResponse<boolean>>>(
      `/api/admin/support/AddSupportMembership`,
      { userId }
    );
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while adding support membership.');
  };

  cancelSupportMembership = async (userId: string) => {
    const response = await this.axiosInstance.post<UserDataPost, AxiosResponse<ApiResponse<boolean>>>(
      `/api/admin/support/CancelSupportMembership`,
      { userId }
    );
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while cancelling support membership.');
  };

  terminateSubscription = async (userId: string) => {
    const response = await this.axiosInstance.post<UserDataPost, AxiosResponse<ApiResponse<UserDataResponse>>>(
      `/api/Payment/TerminateSubscription`,
      { userId: userId }
    );
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while terminating subscription.');
  };


  updatePlayerLevelMaster = async (userId: string) => {
    const response = await this.axiosInstance.post<UserDataPost, AxiosResponse<ApiResponse<UserDataResponse>>>(
      `/api/admin/support/UpdatePlayerLevelMaster`,
      { userId: userId }
    );
  
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while updating player level master.');
  };

  getUserIdByEmail = async (userEmail: string) => {
    const response = await this.axiosInstance.get<ApiResponse<string>>(`/api/admin/user/GetIdByEmail?email=${userEmail}`);

    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while getting user ID by email.');
  };

  getUserEmailById = async (userId: string) => {
    const response = await this.axiosInstance.get<ApiResponse<string>>(`/api/admin/user/GetEmailById?id=${userId}`);

    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while getting user email by ID.');
  };

  getAllCertificates = async () => {
    const response = await this.axiosInstance.get<ApiResponse<{ id: string; title: string }[]>>(
      `/api/admin/support/get-all-certificates`
    );
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while getting certificates.');
  };

  grantCertificationTestAccess = async (userId: string, certificationTestId: string) => {
    const response = await this.axiosInstance.post(
      `/api/admin/support/give-certification-test-access`,
      { userId, certificationTestId }
    );
    if (response.status === 200 && (!response.data || !response.data.isError)) {
      return;
    }
    throw Error('There was an internal error while granting certification test access.');
  };

  grantCertificate = async (userId: string, certificateId: string) => {
    const response = await this.axiosInstance.post(
      `/api/admin/support/give-certificate-access`,
      { userId, certificateId }
    );
    if (response.status === 200 && (!response.data || !response.data.isError)) {
      return;
    }
    throw Error('There was an internal error while granting certificate.');
  };

  revokeCertificationTestAccess = async (userId: string, certificationTestId: string) => {
    const response = await this.axiosInstance.post(
      `/api/admin/support/revoke-certification-test-access`,
      { userId, certificationTestId }
    );
    if (response.status === 200 && (!response.data || !response.data.isError)) {
      return;
    }
    throw Error('There was an internal error while revoking certification test access.');
  };

  revokeCertificate = async (userId: string, certificateId: string) => {
    const response = await this.axiosInstance.post(
      `/api/admin/support/revoke-certificate-access`,
      { userId, certificateId }
    );
    if (response.status === 200 && (!response.data || !response.data.isError)) {
      return;
    }
    throw Error('There was an internal error while revoking certificate.');
  };

  generateCertificatePdf = async (userId: string) => {
    const response = await this.axiosInstance.get(`/api/certificate-pdf/admin/master-certificate/${userId}`,
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      responseType: 'blob',
    });

    if (response.data) {
      if (response.status === 400) {
        return {
          status: 'error',
          data: JSON.parse(await response.data.text()),
        };
      } else if (response.status === 200 && response.data.type === 'application/pdf') {
        return { status: 'success', data: response.data };
      }
    }
    throw Error('There was an internal error while generating master certificate PDF.');
  };
}

export default ManageUserDataService;
